import React, { useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { useGetUserSettings } from '../hooks/useBackend';
import analyticsService from '../services/analyticsService';
import LoadingSpinner from './shared/LoadingSpinner';
import { isOnboardingIncomplete } from '../utils/onboardingStatus';
import { getAppHomeRoute } from './DashboardSidebar/dashboardNavConfig';

const getBackendUrl = () => process.env.REACT_APP_BACKEND_URL || '';

const GoogleAuthCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setUser } = useContext(AuthContext);
  const { getUserSettings } = useGetUserSettings();

  useEffect(() => {
    const code = searchParams.get('code');
    const legacyToken = searchParams.get('token');
    const error = searchParams.get('error');
    const redirect = searchParams.get('redirect');

    if (error) {
      console.error('Google OAuth error:', error);
      const errorMessage = searchParams.get('message') || 'Google authentication failed';
      navigate(`/login?error=${encodeURIComponent(errorMessage)}`, { replace: true });
      return;
    }

    const initializeUser = async () => {
      try {
        let token = legacyToken;

        // Prefer short-lived exchange code (session JWT never appears in the URL)
        if (code) {
          const exchangeRes = await fetch(`${getBackendUrl()}/auth/google/exchange`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code }),
          });
          const exchangeJson = await exchangeRes.json().catch(() => ({}));
          if (!exchangeRes.ok || !exchangeJson.token) {
            throw new Error(exchangeJson.error || 'Failed to exchange Google auth code');
          }
          token = exchangeJson.token;
        }

        if (!token) {
          console.error('No token/code received from Google OAuth callback');
          navigate('/login?error=no_token', { replace: true });
          return;
        }

        const userData = { token };

        const settings = await getUserSettings(token);
        if (settings) {
          const fullUserData = {
            ...userData,
            ...settings,
            token: token
          };
          setUser(fullUserData);

          analyticsService.init(settings.id, token);
          analyticsService.identifyUser(settings.id, {
            email: settings.email,
            subscription_status: settings.subscriptionStatus || 'unknown',
            has_tradestation: settings.hasTradeStationCredentials || false,
            early_access: settings.early_access || false,
            beta_user: settings.beta_user || false,
            auth_provider: 'google',
          });

          analyticsService.trackTradingEvent('user_logged_in', {
            user_id: settings.id,
            email: settings.email,
            auth_provider: 'google',
          });

          const allowed =
            redirect &&
            /^\/[a-z0-9/-]*$/i.test(redirect) &&
            !redirect.includes('//');
          const dest = allowed
            ? redirect
            : (isOnboardingIncomplete(fullUserData, null) ? '/onboarding' : getAppHomeRoute(fullUserData));
          navigate(dest, { replace: true });
        } else {
          throw new Error('Failed to fetch user settings');
        }
      } catch (err) {
        console.error('Error initializing user from Google OAuth:', err);
        navigate('/login?error=initialization_failed', { replace: true });
      }
    };

    initializeUser();
  }, [searchParams, navigate, setUser, getUserSettings]);

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      background: 'var(--bg-primary, #0f172a)',
      zIndex: 2000
    }}>
      <LoadingSpinner />
    </div>
  );
};

export default GoogleAuthCallback;
